import { createSlice } from '@reduxjs/toolkit';

import {
  fetchAdminDashboard,
} from './adminDashboardThunk';

import {
  AdminDashboardData,
  AdminDashboardSummary,
  AdminDashboardAIAnalysis,
  AdminDashboardToday,
  AdminDashboardVoiceAgent,
  TopHiringRole,
  RecentActivity,
} from '../types/adminDashboard.types';

interface AdminDashboardState {
  data: AdminDashboardData | null;
  loading: boolean;
  error: string | null;
}

const initialState: AdminDashboardState = {
  data: null,
  loading: false,
  error: null,
};

const numberValue = (
  value: unknown
): number => {
  return typeof value === 'number'
    ? value
    : Number(value) || 0;
};

const stringValue = (
  value: unknown
): string => {
  return typeof value === 'string'
    ? value
    : '';
};

const normalizeAdminDashboard = (
  payload: any
): AdminDashboardData => {
  const summary: AdminDashboardSummary = {
    totalApplications: numberValue(
      payload?.summary?.totalApplications
    ),

    todaysApplications: numberValue(
      payload?.summary?.todaysApplications
    ),

    pendingATS: numberValue(
      payload?.summary?.pendingATS
    ),

    totalJobs: numberValue(
      payload?.summary?.totalJobs
    ),

    activeJobs: numberValue(
      payload?.summary?.activeJobs
    ),

    selected: numberValue(
      payload?.summary?.selected
    ),

    selectionRate: numberValue(
      payload?.summary?.selectionRate
    ),
  };

  const aiAnalysis: AdminDashboardAIAnalysis = {
    analyzed: numberValue(
      payload?.aiAnalysis?.analyzed
    ),

    shortlisted: numberValue(
      payload?.aiAnalysis?.shortlisted
    ),

    rejected: numberValue(
      payload?.aiAnalysis?.rejected
    ),

    hold: numberValue(
      payload?.aiAnalysis?.hold
    ),
  };

  const today: AdminDashboardToday = {
    applications: numberValue(
      payload?.today?.applications
    ),

    interviews: numberValue(
      payload?.today?.interviews
    ),

    voiceCalls: numberValue(
      payload?.today?.voiceCalls
    ),
  };

  const topHiringRoles: TopHiringRole[] =
    Array.isArray(payload?.topHiringRoles)
      ? payload.topHiringRoles.map(
        (item: any) => ({
          role: stringValue(
            item?.role
          ),

          applications: numberValue(
            item?.applications
          ),

          pending: numberValue(
            item?.pending
          ),

          selected: numberValue(
            item?.selected
          ),

          rejected: numberValue(
            item?.rejected
          ),
        })
      )
      : [];

  const voiceAgent: AdminDashboardVoiceAgent = {
    totalCalls: numberValue(
      payload?.voiceAgent?.totalCalls
    ),

    completed: numberValue(
      payload?.voiceAgent?.completed
    ),

    successRate: numberValue(
      payload?.voiceAgent?.successRate
    ),
  };

  const recentActivity: RecentActivity[] =
    Array.isArray(
      payload?.recentActivity
    )
      ? payload.recentActivity
      : [];

  return {
    summary,
    aiAnalysis,
    today,
    topHiringRoles,
    voiceAgent,
    recentActivity,
  };
};

const adminDashboardSlice = createSlice({
  name: 'adminDashboard',

  initialState,

  reducers: {
    clearAdminDashboardError: (state) => {
      state.error = null;
    },

    clearAdminDashboard: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.data = normalizeAdminDashboard(action.payload);
      })

      .addCase(fetchAdminDashboard.rejected, (state, action) => {
        state.loading = false;

        // Condition-based rejection is not an API failure.
        if (action.meta.condition) return;

        state.error =
          action.payload ||
          'Failed to fetch admin dashboard';
      });
  },
});

export const {
  clearAdminDashboardError,
  clearAdminDashboard,
} = adminDashboardSlice.actions;

export default adminDashboardSlice.reducer;